import type { EventParticipantRole } from 'models';
import { Button, Col, EntitySnippet, MinusCircleIcon, Row, Select } from 'ui';
import { images } from 'common/utils';
import type {
  InvitationSearchEmailOption,
  InvitationSearchEmailOptionLabel,
  InvitationSearchUserOption,
  InvitationSearchUserOptionLabel,
} from '../../types';
import { InvitationsListSection } from '../invitations-list-section';
import { EventParticipantRoleSelectOption } from './event-participant-role-select-option';
import { type EventParticipantRoleOption, useGetEventParticipantRoleOption } from './hooks';

type IndividualInvitation = InvitationSearchEmailOptionLabel | InvitationSearchUserOptionLabel;
type IndividualInvitationWithRole = InvitationSearchEmailOption | InvitationSearchUserOption;

const hasRole = (invitation: IndividualInvitation): invitation is IndividualInvitationWithRole => 'role' in invitation;

type P = {
  invitations: IndividualInvitation[];
  onRemove: (key: string) => void;
  onRoleChange?: (key: string, role: EventParticipantRole) => void;
};

const roleOptions: EventParticipantRole[] = ['moderator', 'presenter', 'attendee'];

const IndividualInvitationsList = ({ invitations, onRemove, onRoleChange }: P) => {
  const getEventParticipantRoleOption = useGetEventParticipantRoleOption();

  return (
    <InvitationsListSection<IndividualInvitation>
      getKey={(invitation) => invitation.value}
      items={invitations}
      renderItem={(invitation) => (
        <Row align="middle" gutter="ms" justify="space-between" wrap={false}>
          <Col flex="1">
            <EntitySnippet
              avatarProps={{
                photo:
                  invitation.type === 'user'
                    ? (invitation.profilePhoto ?? images.default.userProfilePhoto)
                    : images.default.userProfilePhoto,
                size: 32,
              }}
              descriptionProps={{ children: invitation.type === 'user' ? invitation.email : 'Non-registered user' }}
              titleProps={{ children: invitation.label, weight: 'medium' }}
            />
          </Col>
          {hasRole(invitation) && onRoleChange ? (
            <Col span={7}>
              <Select<EventParticipantRole, EventParticipantRoleOption>
                onChange={(role) => {
                  onRoleChange(invitation.value, role);
                }}
                optionRender={(option) => <EventParticipantRoleSelectOption option={option.data} />}
                options={roleOptions.map((role) => getEventParticipantRoleOption(role))}
                value={invitation.role}
              />
            </Col>
          ) : null}
          <Col>
            <Button
              icon={<MinusCircleIcon type="error" />}
              onClick={() => {
                onRemove(invitation.value);
              }}
              type="link-secondary"
            />
          </Col>
        </Row>
      )}
      title="Individual Invitations"
    />
  );
};

export default IndividualInvitationsList;
